import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress
} from '@mui/material';
import { useSelector } from 'react-redux';
import realDataAccountingService from '../../services/realDataAccountingService';
import accountingDataService from '../../services/accountingDataService';

const DataTest = () => {
  const [testResults, setTestResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get Redux data
  const reduxData = {
    students: useSelector(state => state.students) || [],
    teachers: useSelector(state => state.teachers) || [],
    expenses: useSelector(state => state.expenses) || [],
    sales: useSelector(state => state.sales?.sales) || [],
    assets: useSelector(state => state.assets?.assets) || []
  };

  const { schooldetails } = useSelector(state => state.schooldetails);
  const currentSchool = schooldetails && schooldetails.length > 0 ? schooldetails[0] : null;

  const runDataTest = async () => {
    if (!currentSchool?.schoolID) {
      setError('No school selected');
      return;
    }

    setLoading(true);
    setError(null);
    setTestResults(null);

    try {
      console.log('Starting data test with Redux data:', reduxData);
      
      const endDate = new Date();
      const startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

      // Test 1: Get all financial data
      console.log('Test 1: Getting all financial data...');
      const financialData = await accountingDataService.getAllFinancialData(
        currentSchool.schoolID,
        startDate,
        endDate,
        reduxData
      );
      console.log('Financial data result:', financialData);

      // Test 2: Generate trial balance
      console.log('Test 2: Generating trial balance...');
      const trialBalance = await realDataAccountingService.generateRealTrialBalance(
        currentSchool.schoolID,
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0],
        reduxData
      );
      console.log('Trial balance result:', trialBalance);

      // Test 3: Get dashboard data
      console.log('Test 3: Getting dashboard data...');
      const dashboardData = await realDataAccountingService.getRealDashboardData(
        currentSchool.schoolID,
        reduxData
      );
      console.log('Dashboard data result:', dashboardData);

      setTestResults({
        financialData,
        trialBalance,
        dashboardData,
        reduxDataSummary: {
          studentsCount: reduxData.students.length,
          teachersCount: reduxData.teachers.length,
          expensesCount: reduxData.expenses.length,
          salesCount: reduxData.sales.length,
          assetsCount: reduxData.assets.length
        }
      });

    } catch (err) {
      console.error('Data test error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Data Integration Test
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Redux Data Summary
          </Typography>
          <Typography variant="body2">
            Students: {reduxData.students.length} | 
            Teachers: {reduxData.teachers.length} | 
            Expenses: {reduxData.expenses.length} | 
            Sales: {reduxData.sales.length} | 
            Assets: {reduxData.assets.length}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Current School: {currentSchool?.schoolname || 'None selected'}
          </Typography>
        </CardContent>
      </Card>

      <Button 
        variant="contained" 
        onClick={runDataTest}
        disabled={loading || !currentSchool?.schoolID}
        sx={{ mb: 3 }}
      >
        {loading ? 'Testing...' : 'Run Data Test'}
      </Button>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <CircularProgress size={24} />
          <Typography>Running data integration tests...</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {testResults && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Test Results
            </Typography>
            
            <Typography variant="subtitle1" gutterBottom>
              Financial Data Summary:
            </Typography>
            <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px', backgroundColor: '#f5f5f5', padding: '10px' }}>
              {JSON.stringify(testResults.financialData, null, 2)}
            </pre>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Trial Balance Summary:
            </Typography>
            <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px', backgroundColor: '#f5f5f5', padding: '10px' }}>
              {JSON.stringify({
                totalDebits: testResults.trialBalance?.totalDebits,
                totalCredits: testResults.trialBalance?.totalCredits,
                accountsCount: testResults.trialBalance?.trialBalance?.length
              }, null, 2)}
            </pre>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
              Dashboard Data Summary:
            </Typography>
            <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px', backgroundColor: '#f5f5f5', padding: '10px' }}>
              {JSON.stringify(testResults.dashboardData, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default DataTest;
